import { CRM_ADAPTERS } from "./emailThumbnail.adapters";
import {
  getTemplateFields,
  parseTemplate,
  TemplateParseError,
} from "./emailThumbnail.template";
import {
  CrmAdapter,
  EmailThumbnailConfig,
  SizingBucket,
  TextLayer,
} from "./emailThumbnail.types";
import ipaddr from "ipaddr.js";

type ParsedNode = {
  nodeName: string;
  tagName?: string;
  value?: string;
  data?: string;
  attrs?: Array<{ name: string; value: string }>;
  childNodes?: ParsedNode[];
};

const parse5 = require("parse5") as {
  parseFragment: (html: string) => { childNodes: ParsedNode[] };
};

export type EmailThumbnailDiagnosticCode =
  | "invalid-background-url"
  | "invalid-destination-url"
  | "unmapped-field"
  | "invalid-crm-token"
  | "invalid-color"
  | "invalid-dimensions"
  | "invalid-text-style"
  | "invalid-sizing-buckets"
  | "invalid-configuration"
  | "invalid-template"
  | "missing-visible-personalization"
  | "unsupported-sizing-strategy"
  | "missing-bucket-field"
  | "document-wrapper"
  | "additional-root-node"
  | "forbidden-markup"
  | "forbidden-attribute"
  | "data-url"
  | "missing-vml"
  | "long-sample"
  | "max-lines-exceeded"
  | "sizing-approximation"
  | "low-contrast"
  | "large-dimensions"
  | "unusual-aspect-ratio"
  | "outlook-approximation";

export type EmailThumbnailDiagnostic = {
  severity: "error" | "warning";
  code: EmailThumbnailDiagnosticCode;
  message: string;
  field?: string;
};

type GeneratedFragmentTokenContext = {
  adapter: CrmAdapter;
  tokens: readonly string[];
};

const FONT_FAMILIES = [
  "Arial, Helvetica, sans-serif",
  "Georgia, 'Times New Roman', serif",
  "'Courier New', Courier, monospace",
] as const;
const FONT_SIZES = [
  12, 14, 16, 18, 20, 22, 24, 26, 28, 30, 32, 34, 36, 38, 40, 42, 44, 48, 52,
  56, 60, 64,
];
const LINE_HEIGHTS = [
  14, 16, 18, 20, 22, 24, 26, 28, 30, 32, 34, 36, 38, 40, 42, 44, 48, 52, 56,
  60, 64, 68, 72,
];
const PADDINGS = [0, 4, 8, 12, 16, 20, 24, 28, 32, 36, 40, 44, 48, 56, 64];
const PLAY_SIZES = [16, 20, 24, 28, 32, 36, 40, 44, 48, 56, 64, 72, 80];
const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;
const MERGE_TOKEN =
  /\{\{\{[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*\}\}\}|\{\{(?:lead\.)?[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*\}\}|%%[A-Za-z_][A-Za-z0-9_.]*%%|@[A-Za-z_][A-Za-z0-9_.]*@|\*\|[A-Z_][A-Z0-9_]*\|\*/g;
const CANONICAL_FIELD =
  /\{\{[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*\}\}/g;
const RAW_CRM_TOKEN =
  /\{\{\{[^}]+\}\}\}|\{\{(?:lead\.|Recipient\.)[^}]+\}\}|%%[^%]+%%|@[^@]+@|\*\|[^|]+\|\*/;
type IpCidr = readonly [string, number];
type IpReachabilityRule = {
  cidr: IpCidr;
  isGloballyReachable: boolean;
};

// IANA IPv4/IPv6 Special-Purpose Address Space snapshot 2025-10-09,
// https://www.iana.org/assignments/iana-ipv6-special-registry/ (consulted 2026-08-24).
// More-specific entries override their parent CIDR.
// In that snapshot, 5f00::/16 (SRv6 SIDs) is not globally reachable.
const IANA_REACHABILITY_RULES: readonly IpReachabilityRule[] = [
  { cidr: ["0.0.0.0", 8], isGloballyReachable: false },
  { cidr: ["10.0.0.0", 8], isGloballyReachable: false },
  { cidr: ["100.64.0.0", 10], isGloballyReachable: false },
  { cidr: ["127.0.0.0", 8], isGloballyReachable: false },
  { cidr: ["169.254.0.0", 16], isGloballyReachable: false },
  { cidr: ["172.16.0.0", 12], isGloballyReachable: false },
  { cidr: ["192.0.0.0", 24], isGloballyReachable: false },
  { cidr: ["192.0.0.9", 32], isGloballyReachable: true },
  { cidr: ["192.0.0.10", 32], isGloballyReachable: true },
  { cidr: ["192.0.2.0", 24], isGloballyReachable: false },
  { cidr: ["192.88.99.0", 24], isGloballyReachable: false },
  { cidr: ["192.88.99.2", 32], isGloballyReachable: true },
  { cidr: ["192.168.0.0", 16], isGloballyReachable: false },
  { cidr: ["198.18.0.0", 15], isGloballyReachable: false },
  { cidr: ["198.51.100.0", 24], isGloballyReachable: false },
  { cidr: ["203.0.113.0", 24], isGloballyReachable: false },
  { cidr: ["224.0.0.0", 4], isGloballyReachable: false },
  { cidr: ["240.0.0.0", 4], isGloballyReachable: false },
  { cidr: ["::", 96], isGloballyReachable: false },
  { cidr: ["64:ff9b::", 96], isGloballyReachable: true },
  { cidr: ["64:ff9b:1::", 48], isGloballyReachable: false },
  { cidr: ["100::", 64], isGloballyReachable: false },
  { cidr: ["100:0:0:1::", 64], isGloballyReachable: false },
  { cidr: ["2001::", 23], isGloballyReachable: false },
  { cidr: ["2001:1::1", 128], isGloballyReachable: true },
  { cidr: ["2001:1::2", 128], isGloballyReachable: true },
  { cidr: ["2001:1::3", 128], isGloballyReachable: true },
  { cidr: ["2001:3::", 32], isGloballyReachable: true },
  { cidr: ["2001:4:112::", 48], isGloballyReachable: true },
  { cidr: ["2001:20::", 28], isGloballyReachable: true },
  { cidr: ["2001:30::", 28], isGloballyReachable: true },
  { cidr: ["2001:db8::", 32], isGloballyReachable: false },
  { cidr: ["2002::", 16], isGloballyReachable: false },
  { cidr: ["2620:4f:8000::", 48], isGloballyReachable: true },
  { cidr: ["3fff::", 20], isGloballyReachable: false },
  { cidr: ["5f00::", 16], isGloballyReachable: false },
  { cidr: ["fc00::", 7], isGloballyReachable: false },
  { cidr: ["fe80::", 10], isGloballyReachable: false },
  { cidr: ["fec0::", 10], isGloballyReachable: false },
  { cidr: ["ff00::", 8], isGloballyReachable: false },
];
const IPV6_GLOBAL_UNICAST_SCOPE: IpCidr = ["2000::", 3];
const NAT64_WELL_KNOWN_PREFIX: IpCidr = ["64:ff9b::", 96];
const ALLOWED_ELEMENTS: Record<string, readonly string[]> = {
  table: [
    "role",
    "border",
    "cellpadding",
    "cellspacing",
    "width",
    "height",
    "style",
  ],
  tr: [],
  tbody: [],
  td: ["align", "valign", "background", "width", "height", "style"],
  a: ["href", "title", "aria-label", "style"],
  span: ["style"],
  br: [],
  "v:rect": ["xmlns:v", "fill", "stroke", "style"],
  "v:fill": ["type", "src", "color"],
  "v:textbox": ["inset"],
};
const STYLE_PROPERTIES: Record<string, readonly string[]> = {
  table: ["width", "height", "border-collapse"],
  td: [
    "background-color",
    "background-image",
    "background-position",
    "background-repeat",
    "background-size",
    "padding",
  ],
  a: ["text-decoration", "display"],
  span: [
    "font-family",
    "font-weight",
    "font-size",
    "line-height",
    "color",
    "display",
    "width",
    "height",
    "text-align",
    "background-color",
  ],
  "v:rect": ["width", "height"],
};

const error = (
  code: EmailThumbnailDiagnosticCode,
  message: string,
  field?: string
): EmailThumbnailDiagnostic => ({ severity: "error", code, message, field });

const warning = (
  code: EmailThumbnailDiagnosticCode,
  message: string,
  field?: string
): EmailThumbnailDiagnostic => ({ severity: "warning", code, message, field });

const relativeLuminance = (color: string): number => {
  const channels = [1, 3, 5].map(
    (index) => parseInt(color.slice(index, index + 2), 16) / 255
  );
  const [red, green, blue] = channels.map((channel) =>
    channel <= 0.03928
      ? channel / 12.92
      : Math.pow((channel + 0.055) / 1.055, 2.4)
  );
  return red * 0.2126 + green * 0.7152 + blue * 0.0722;
};

const contrastRatio = (foreground: string, background: string): number => {
  const light = Math.max(
    relativeLuminance(foreground),
    relativeLuminance(background)
  );
  const dark = Math.min(
    relativeLuminance(foreground),
    relativeLuminance(background)
  );
  return (light + 0.05) / (dark + 0.05);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isTextLayerShape = (value: unknown): value is TextLayer =>
  isRecord(value) &&
  typeof value.template === "string" &&
  typeof value.fontFamily === "string" &&
  typeof value.fontWeight === "string" &&
  typeof value.fontSize === "number" &&
  typeof value.lineHeight === "number" &&
  typeof value.color === "string" &&
  typeof value.horizontalAlignment === "string" &&
  typeof value.verticalSlot === "string" &&
  typeof value.padding === "number";

const isStringMap = (value: unknown): value is Record<string, string> =>
  isRecord(value) &&
  Object.values(value).every((entry) => typeof entry === "string");

const isRuntimeConfig = (config: unknown): config is EmailThumbnailConfig =>
  isRecord(config) &&
  config.version === 1 &&
  typeof config.backgroundUrl === "string" &&
  typeof config.destinationUrl === "string" &&
  typeof config.width === "number" &&
  typeof config.height === "number" &&
  typeof config.maxLines === "number" &&
  typeof config.fallbackColor === "string" &&
  typeof config.fallbackText === "string" &&
  isTextLayerShape(config.headline) &&
  (config.supportingLine === null || isTextLayerShape(config.supportingLine)) &&
  isRecord(config.playIndicator) &&
  typeof config.playIndicator.enabled === "boolean" &&
  typeof config.playIndicator.size === "number" &&
  typeof config.playIndicator.foregroundColor === "string" &&
  typeof config.playIndicator.backgroundColor === "string" &&
  typeof config.crmId === "string" &&
  Object.prototype.hasOwnProperty.call(CRM_ADAPTERS, config.crmId) &&
  isStringMap(config.fieldMappings) &&
  (config.sizingStrategy === "fixed" ||
    config.sizingStrategy === "native-length" ||
    config.sizingStrategy === "precomputed-bucket") &&
  (config.bucketField === null || typeof config.bucketField === "string") &&
  Array.isArray(config.sizingBuckets) &&
  config.sizingBuckets.every(
    (bucket) =>
      isRecord(bucket) &&
      typeof bucket.maxCharacters === "number" &&
      typeof bucket.fontSize === "number" &&
      typeof bucket.lineHeight === "number"
  ) &&
  isStringMap(config.sampleData);

const containsMergeToken = (value: string): boolean => {
  const hasToken = MERGE_TOKEN.test(value);
  MERGE_TOKEN.lastIndex = 0;
  return hasToken;
};

const isInCidr = (
  address: ReturnType<typeof ipaddr.process>,
  [network, prefixLength]: IpCidr
): boolean => {
  const networkAddress = ipaddr.parse(network);
  if (address.kind() !== networkAddress.kind()) {
    return false;
  }
  const addressBytes = address.toByteArray();
  const networkBytes = networkAddress.toByteArray();
  const fullBytes = Math.floor(prefixLength / 8);
  const partialBits = prefixLength % 8;

  return (
    addressBytes
      .slice(0, fullBytes)
      .every((byte, index) => byte === networkBytes[index]) &&
    (partialBits === 0 ||
      (addressBytes[fullBytes] & (0xff << (8 - partialBits))) ===
        (networkBytes[fullBytes] & (0xff << (8 - partialBits))))
  );
};

const isGloballyReachableIp = (
  address: ReturnType<typeof ipaddr.process>
): boolean => {
  if (address.kind() === "ipv6" && isInCidr(address, NAT64_WELL_KNOWN_PREFIX)) {
    return isGloballyReachableIp(
      ipaddr.fromByteArray(address.toByteArray().slice(-4))
    );
  }
  const matchingRule =
    IANA_REACHABILITY_RULES.reduce<IpReachabilityRule | null>(
      (mostSpecific, rule) =>
        isInCidr(address, rule.cidr) &&
        (!mostSpecific || rule.cidr[1] > mostSpecific.cidr[1])
          ? rule
          : mostSpecific,
      null
    );
  if (matchingRule) {
    return matchingRule.isGloballyReachable;
  }
  return (
    address.kind() === "ipv4" || isInCidr(address, IPV6_GLOBAL_UNICAST_SCOPE)
  );
};

const isPublicHost = (hostname: string): boolean => {
  const host = hostname
    .replace(/^\[|\]$/g, "")
    .replace(/\.+$/, "")
    .toLowerCase();
  if (
    !host ||
    host === "localhost" ||
    host.endsWith(".localhost") ||
    host.endsWith(".local")
  ) {
    return false;
  }
  if (ipaddr.isValid(host)) {
    return isGloballyReachableIp(ipaddr.process(host));
  }
  if (/^[\d.]+$/.test(host)) {
    return false;
  }
  return host.includes(".");
};

const getHttpsAuthority = (value: string): string | null => {
  const authorityMatch = /^https:\/\/([^/?#]*)(?:[/?#]|$)/i.exec(value);
  return authorityMatch ? authorityMatch[1] : null;
};

const isHttpsUrl = (value: string): boolean => {
  if (!value || value !== value.trim() || /[\s'"<>()]/.test(value)) {
    return false;
  }

  const authority = getHttpsAuthority(value);
  if (!authority) {
    return false;
  }
  if (
    authority.includes("@") ||
    containsMergeToken(authority) ||
    /[{}]/.test(authority)
  ) {
    return false;
  }

  if (containsMergeToken(value) || /[{}]/.test(value)) {
    return false;
  }
  try {
    const parsed = new URL(value);
    return (
      parsed.protocol === "https:" &&
      !parsed.username &&
      !parsed.password &&
      isPublicHost(parsed.hostname)
    );
  } catch (_error) {
    return false;
  }
};

const isHttpsUrlWithValidatedTokens = (
  value: string,
  adapter: CrmAdapter,
  tokens: readonly string[]
): boolean => {
  const authority = getHttpsAuthority(value);
  if (!authority || authority.includes("@") || /[\s{}'"<>()]/.test(authority)) {
    return false;
  }
  const validatedTokens = Array.from(new Set(tokens))
    .filter((token) => adapter.isValidToken(token))
    .sort((left, right) => right.length - left.length);
  if (validatedTokens.some((token) => authority.includes(token))) {
    return false;
  }
  const masked = validatedTokens.reduce(
    (url, token) => url.split(token).join("email-thumbnail-field"),
    value
  );
  if (/[{}]/.test(masked) || containsMergeToken(masked)) {
    return false;
  }
  return isHttpsUrl(masked);
};

const getFields = (
  template: string,
  diagnostics: EmailThumbnailDiagnostic[]
): string[] => {
  try {
    return getTemplateFields(template);
  } catch (caught) {
    const detail =
      caught instanceof TemplateParseError
        ? caught.message
        : "Invalid template";
    diagnostics.push(error("invalid-template", detail));
    return [];
  }
};

const validateTextLayer = (
  layer: TextLayer,
  diagnostics: EmailThumbnailDiagnostic[]
): void => {
  const isKnownFont = (FONT_FAMILIES as readonly string[]).includes(
    layer.fontFamily
  );
  const validStyle =
    isKnownFont &&
    (layer.fontWeight === "normal" || layer.fontWeight === "bold") &&
    FONT_SIZES.includes(layer.fontSize) &&
    LINE_HEIGHTS.includes(layer.lineHeight) &&
    PADDINGS.includes(layer.padding) &&
    (layer.horizontalAlignment === "left" ||
      layer.horizontalAlignment === "center" ||
      layer.horizontalAlignment === "right") &&
    (layer.verticalSlot === "top" ||
      layer.verticalSlot === "center" ||
      layer.verticalSlot === "bottom");

  if (!validStyle) {
    diagnostics.push(
      error(
        "invalid-text-style",
        "Text styles must use the generator's allowlisted values"
      )
    );
  }
  if (!HEX_COLOR.test(layer.color)) {
    diagnostics.push(
      error("invalid-color", "Text colors must be six-digit hexadecimal colors")
    );
  }
};

const validBuckets = (buckets: SizingBucket[]): boolean =>
  buckets.length === 3 &&
  buckets.every(
    (bucket, index) =>
      Number.isInteger(bucket.maxCharacters) &&
      bucket.maxCharacters > 0 &&
      (index === 0 ||
        bucket.maxCharacters > buckets[index - 1].maxCharacters) &&
      FONT_SIZES.includes(bucket.fontSize) &&
      LINE_HEIGHTS.includes(bucket.lineHeight)
  );

const renderHeadlineSample = (config: EmailThumbnailConfig): string | null => {
  try {
    return parseTemplate(config.headline.template)
      .map((segment) =>
        segment.type === "text"
          ? segment.value
          : config.sampleData[segment.field] ?? segment.field
      )
      .join("");
  } catch (_error) {
    return null;
  }
};

const hasVisibleMappedPersonalization = (
  layer: TextLayer | null,
  knownFields: Set<string>,
  fieldMappings: Record<string, string>,
  adapter: (typeof CRM_ADAPTERS)[keyof typeof CRM_ADAPTERS]
): boolean => {
  if (!layer || !layer.template.trim()) {
    return false;
  }
  try {
    return getTemplateFields(layer.template).some(
      (field) =>
        knownFields.has(field) &&
        typeof fieldMappings[field] === "string" &&
        adapter.isValidToken(fieldMappings[field])
    );
  } catch (_error) {
    return false;
  }
};

export const validateEmailThumbnailConfig = (
  config: EmailThumbnailConfig,
  schemaFields: string[]
): EmailThumbnailDiagnostic[] => {
  if (
    !isRuntimeConfig(config) ||
    !Array.isArray(schemaFields) ||
    !schemaFields.every((field) => typeof field === "string")
  ) {
    return [
      error(
        "invalid-configuration",
        "Email thumbnail configuration has an invalid persisted shape"
      ),
    ];
  }
  const diagnostics: EmailThumbnailDiagnostic[] = [];
  const knownFields = new Set(schemaFields);
  const adapter = CRM_ADAPTERS[config.crmId];

  if (!isHttpsUrl(config.backgroundUrl)) {
    diagnostics.push(
      error(
        "invalid-background-url",
        "Background URL must be an absolute HTTPS URL"
      )
    );
  }
  const destinationFields = getFields(config.destinationUrl, diagnostics);
  if (
    !Number.isInteger(config.width) ||
    !Number.isInteger(config.height) ||
    config.width < 1 ||
    config.width > 1200 ||
    config.height < 1 ||
    config.height > 1200
  ) {
    diagnostics.push(
      error(
        "invalid-dimensions",
        "Dimensions must be whole pixels between 1 and 1200"
      )
    );
  }
  if (
    !Number.isInteger(config.maxLines) ||
    config.maxLines < 1 ||
    config.maxLines > 6
  ) {
    diagnostics.push(
      error(
        "invalid-text-style",
        "Maximum lines must be a whole number between 1 and 6"
      )
    );
  }
  if (!HEX_COLOR.test(config.fallbackColor)) {
    diagnostics.push(
      error(
        "invalid-color",
        "Fallback color must be a six-digit hexadecimal color"
      )
    );
  }
  if (!config.fallbackText.trim()) {
    diagnostics.push(error("invalid-text-style", "Fallback text is required"));
  }

  validateTextLayer(config.headline, diagnostics);
  if (config.supportingLine) {
    validateTextLayer(config.supportingLine, diagnostics);
  }
  if (
    !PLAY_SIZES.includes(config.playIndicator.size) ||
    !HEX_COLOR.test(config.playIndicator.foregroundColor) ||
    !HEX_COLOR.test(config.playIndicator.backgroundColor)
  ) {
    diagnostics.push(
      error(
        "invalid-text-style",
        "Play indicator values must use the generator's allowlisted values"
      )
    );
  }
  if (!validBuckets(config.sizingBuckets)) {
    diagnostics.push(
      error(
        "invalid-sizing-buckets",
        "Sizing needs three ascending, allowlisted buckets"
      )
    );
  }

  const headlineFields = new Set<string>();
  if (typeof config.headline.template === "string") {
    getFields(config.headline.template, diagnostics).forEach((field) =>
      headlineFields.add(field)
    );
  }
  const fields = new Set<string>(headlineFields);
  [config.destinationUrl, config.supportingLine?.template]
    .filter((template): template is string => typeof template === "string")
    .forEach((template) =>
      getFields(template, diagnostics).forEach((field) => fields.add(field))
    );

  fields.forEach((field) => {
    if (!knownFields.has(field)) {
      diagnostics.push(
        error(
          "unmapped-field",
          `Unknown personalization field: ${field}`,
          field
        )
      );
      return;
    }
    const token = config.fieldMappings[field];
    if (!token) {
      diagnostics.push(
        error("unmapped-field", `No CRM token is mapped for: ${field}`, field)
      );
    } else if (!adapter.isValidToken(token)) {
      diagnostics.push(
        error(
          "invalid-crm-token",
          `Invalid ${adapter.label} token for: ${field}`,
          field
        )
      );
    }
  });

  if (
    !hasVisibleMappedPersonalization(
      config.headline,
      knownFields,
      config.fieldMappings,
      adapter
    ) &&
    !hasVisibleMappedPersonalization(
      config.supportingLine,
      knownFields,
      config.fieldMappings,
      adapter
    )
  ) {
    diagnostics.push(
      error(
        "missing-visible-personalization",
        "Add a mapped personalization field to the headline or supporting text"
      )
    );
  }

  const destinationTokens = destinationFields.map(
    (field) => config.fieldMappings[field]
  );
  const hasValidatedDestinationTokens =
    destinationFields.length > 0 &&
    destinationFields.every(
      (field, index) =>
        knownFields.has(field) &&
        typeof destinationTokens[index] === "string" &&
        adapter.isValidToken(destinationTokens[index])
    );
  const renderedDestination = config.destinationUrl.replace(
    CANONICAL_FIELD,
    (placeholder) => config.fieldMappings[placeholder.slice(2, -2)] || ""
  );
  if (
    RAW_CRM_TOKEN.test(config.destinationUrl) ||
    !hasValidatedDestinationTokens ||
    !isHttpsUrlWithValidatedTokens(
      renderedDestination,
      adapter,
      destinationTokens.filter(
        (token): token is string => typeof token === "string"
      )
    )
  ) {
    diagnostics.push(
      error(
        "invalid-destination-url",
        "Rendered destination URL must be absolute HTTPS"
      )
    );
  }

  if (
    config.sizingStrategy === "native-length" &&
    !adapter.capabilities.nativeLength
  ) {
    diagnostics.push(
      error(
        "unsupported-sizing-strategy",
        `${adapter.label} does not support native length rules`
      )
    );
  }
  if (
    config.sizingStrategy === "precomputed-bucket" &&
    !adapter.capabilities.precomputedBucket
  ) {
    diagnostics.push(
      error(
        "unsupported-sizing-strategy",
        `${adapter.label} does not support precomputed buckets`
      )
    );
  }
  if (config.sizingStrategy === "precomputed-bucket") {
    const bucketField = config.bucketField;
    const token = bucketField ? config.fieldMappings[bucketField] : undefined;
    if (
      !bucketField ||
      !knownFields.has(bucketField) ||
      !token ||
      !adapter.isValidToken(token)
    ) {
      diagnostics.push(
        error(
          "missing-bucket-field",
          "Precomputed sizing requires a mapped bucket field",
          bucketField || undefined
        )
      );
    }
  }

  const usedSamples = Array.from(fields)
    .filter((field) =>
      Object.prototype.hasOwnProperty.call(config.sampleData, field)
    )
    .map((field) => ({ field, value: config.sampleData[field] }));
  const finalSizingBucket = config.sizingBuckets[2];
  const longSampleThreshold =
    finalSizingBucket &&
    Number.isSafeInteger(finalSizingBucket.maxCharacters) &&
    finalSizingBucket.maxCharacters >= 1 &&
    finalSizingBucket.maxCharacters <= 1200
      ? finalSizingBucket.maxCharacters
      : 42;
  const longSampleFields = usedSamples.filter(
    ({ value }) => Array.from(value).length > longSampleThreshold
  );
  longSampleFields.forEach(({ field }) => {
    diagnostics.push(
      warning(
        "long-sample",
        `Sample copy exceeds ${longSampleThreshold} characters; shorten fallback copy or verify wrapping`,
        field
      )
    );
  });

  const renderedHeadlineSample = renderHeadlineSample(config);
  if (
    renderedHeadlineSample !== null &&
    Array.from(renderedHeadlineSample).length > longSampleThreshold &&
    !longSampleFields.some(({ field }) => headlineFields.has(field))
  ) {
    diagnostics.push(
      warning(
        "long-sample",
        "Rendered headline exceeds the final sizing bucket; shorten fallback copy or verify wrapping",
        "headline"
      )
    );
  }

  const fixedHeadlineSample =
    config.sizingStrategy === "fixed" ? renderedHeadlineSample : null;
  if (fixedHeadlineSample !== null) {
    const availableWidth = Math.max(
      1,
      config.width - config.headline.padding * 2
    );
    const estimatedCharactersPerLine = Math.max(
      1,
      Math.floor(availableWidth / (config.headline.fontSize * 0.55))
    );
    if (
      Array.from(fixedHeadlineSample).length >
      estimatedCharactersPerLine * config.maxLines
    ) {
      diagnostics.push(
        warning(
          "max-lines-exceeded",
          `Rendered headline may exceed the configured ${config.maxLines}-line budget`
        )
      );
    }
  }

  const hasComplexScriptSample = usedSamples.some(({ value }) =>
    /[\u0590-\u08FF\u2E80-\u9FFF]|[\uD800-\uDBFF][\uDC00-\uDFFF]/.test(value)
  );
  if (
    config.headline.fontFamily !== "'Courier New', Courier, monospace" ||
    hasComplexScriptSample
  ) {
    diagnostics.push(
      warning(
        "sizing-approximation",
        "Character-based sizing is approximate for proportional fonts, emoji, CJK, and RTL text"
      )
    );
  }

  if (
    HEX_COLOR.test(config.headline.color) &&
    HEX_COLOR.test(config.fallbackColor) &&
    contrastRatio(config.headline.color, config.fallbackColor) < 4.5
  ) {
    diagnostics.push(
      warning(
        "low-contrast",
        "Headline text has low contrast against the fallback background color"
      )
    );
  }
  if (
    config.supportingLine &&
    HEX_COLOR.test(config.supportingLine.color) &&
    HEX_COLOR.test(config.fallbackColor) &&
    contrastRatio(config.supportingLine.color, config.fallbackColor) < 4.5
  ) {
    diagnostics.push(
      warning(
        "low-contrast",
        "Supporting text has low contrast against the fallback background color"
      )
    );
  }

  if (config.width > 800 || config.height > 600) {
    diagnostics.push(
      warning(
        "large-dimensions",
        "Large email dimensions may be scaled or clipped by some clients"
      )
    );
  }
  const aspectRatio = config.width / config.height;
  if (
    Number.isFinite(aspectRatio) &&
    (aspectRatio > 2.4 || aspectRatio < 0.8)
  ) {
    diagnostics.push(
      warning(
        "unusual-aspect-ratio",
        "This aspect ratio is unusual for an email thumbnail; verify narrow-client behavior"
      )
    );
  }
  diagnostics.push(
    warning(
      "outlook-approximation",
      "Classic Outlook preview is an approximation; generated output uses the VML fallback"
    )
  );

  return diagnostics;
};

export const validateGeneratedFragment = (
  html: string,
  tokenContext?: GeneratedFragmentTokenContext
): EmailThumbnailDiagnostic[] => {
  const diagnostics: EmailThumbnailDiagnostic[] = [];
  const fragment = html.trim();

  if (/<\/?(?:html|head|body)\b|<!doctype/i.test(fragment)) {
    diagnostics.push(
      error(
        "document-wrapper",
        "Generated output must be an embeddable HTML fragment"
      )
    );
  }
  if (
    /<(?:script|form|style|link|svg|iframe|object|embed|video)\b/i.test(
      fragment
    )
  ) {
    diagnostics.push(
      error("forbidden-markup", "Generated output contains forbidden markup")
    );
  }
  validateRawAttributeQuoting(fragment, diagnostics);
  const parsed = parse5.parseFragment(fragment);
  const nonCommentRoots = parsed.childNodes.filter(
    (node) => node.nodeName !== "#comment"
  );
  const root = nonCommentRoots[0];
  if (
    nonCommentRoots.length !== 1 ||
    !root ||
    root.nodeName !== "table" ||
    root.tagName !== "table"
  ) {
    diagnostics.push(
      error(
        "additional-root-node",
        "Generated output must parse to exactly one root table"
      )
    );
  } else {
  }
  nonCommentRoots.forEach((node) =>
    validateParsedNode(node, diagnostics, tokenContext)
  );
  validateConditionalVml(fragment, root, diagnostics);

  return diagnostics;
};

const validateRawAttributeQuoting = (
  fragment: string,
  diagnostics: EmailThumbnailDiagnostic[]
): void => {
  const tagSource = /<[A-Za-z][^>]*>/g;
  let tag: RegExpExecArray | null;
  while ((tag = tagSource.exec(fragment))) {
    if (hasUnquotedAttributeValue(tag[0])) {
      diagnostics.push(
        error(
          "forbidden-attribute",
          "Generated output attributes must use quoted values"
        )
      );
    }
  }
};

const hasUnquotedAttributeValue = (tag: string): boolean => {
  let cursor = 1;
  while (cursor < tag.length && !/\s|>/.test(tag[cursor])) {
    cursor += 1;
  }
  while (cursor < tag.length) {
    while (cursor < tag.length && /\s/.test(tag[cursor])) {
      cursor += 1;
    }
    if (tag[cursor] === "/" || tag[cursor] === ">" || cursor >= tag.length) {
      return false;
    }
    while (cursor < tag.length && !/\s|=|>/.test(tag[cursor])) {
      cursor += 1;
    }
    while (cursor < tag.length && /\s/.test(tag[cursor])) {
      cursor += 1;
    }
    if (tag[cursor] !== "=") {
      return true;
    }
    cursor += 1;
    while (cursor < tag.length && /\s/.test(tag[cursor])) {
      cursor += 1;
    }
    const quote = tag[cursor];
    if (quote !== '"' && quote !== "'") {
      return true;
    }
    cursor += 1;
    while (cursor < tag.length && tag[cursor] !== quote) {
      cursor += 1;
    }
    if (cursor >= tag.length) {
      return true;
    }
    cursor += 1;
  }
  return false;
};

const validateParsedNode = (
  node: ParsedNode,
  diagnostics: EmailThumbnailDiagnostic[],
  tokenContext?: GeneratedFragmentTokenContext
): void => {
  if (node.tagName) {
    const element = node.tagName.toLowerCase();
    const allowedAttributes = ALLOWED_ELEMENTS[element];
    if (!allowedAttributes) {
      diagnostics.push(
        error("forbidden-markup", `Element <${element}> is not allowed`)
      );
    }
    (node.attrs || []).forEach(({ name, value }) => {
      const attribute = name.toLowerCase();
      if (
        attribute.startsWith("on") ||
        !allowedAttributes?.includes(attribute)
      ) {
        diagnostics.push(
          error("forbidden-attribute", `Attribute ${attribute} is not allowed`)
        );
      }
      if (
        attribute === "href" ||
        attribute === "src" ||
        attribute === "background"
      ) {
        validateUrlAttribute(attribute, value, diagnostics, tokenContext);
      }
      if (attribute === "style") {
        validateStyle(element, value, diagnostics);
      }
    });
  }
  (node.childNodes || []).forEach((child) =>
    validateParsedNode(child, diagnostics, tokenContext)
  );
};

const validateUrlAttribute = (
  attribute: string,
  value: string,
  diagnostics: EmailThumbnailDiagnostic[],
  tokenContext?: GeneratedFragmentTokenContext
): void => {
  const destination = attribute === "href";
  if (/^data:/i.test(value)) {
    diagnostics.push(
      error("data-url", "Generated output cannot contain data URLs")
    );
  }
  if (
    !(destination && tokenContext
      ? isHttpsUrlWithValidatedTokens(
          value,
          tokenContext.adapter,
          tokenContext.tokens
        )
      : isHttpsUrl(value))
  ) {
    diagnostics.push(
      error(
        destination ? "invalid-destination-url" : "invalid-background-url",
        "Generated output URLs must be public absolute HTTPS URLs"
      )
    );
  }
};

type CssDeclaration = {
  property: string;
  value: string;
};

const parseCssDeclarations = (style: string): CssDeclaration[] | null => {
  const declarations: string[] = [];
  let start = 0;
  let quote: '"' | "'" | null = null;
  let parentheses = 0;
  let escaped = false;

  for (let index = 0; index < style.length; index += 1) {
    const character = style[index];
    if (escaped) {
      escaped = false;
      continue;
    }
    if (character === "\\") {
      escaped = true;
      continue;
    }
    if (quote) {
      if (character === quote) {
        quote = null;
      }
      continue;
    }
    if (character === '"' || character === "'") {
      quote = character;
      continue;
    }
    if (character === "(") {
      parentheses += 1;
      continue;
    }
    if (character === ")") {
      if (parentheses === 0) {
        return null;
      }
      parentheses -= 1;
      continue;
    }
    if (character === ";" && parentheses === 0) {
      declarations.push(style.slice(start, index));
      start = index + 1;
    }
  }

  if (quote || parentheses !== 0 || escaped) {
    return null;
  }
  declarations.push(style.slice(start));

  const parsed = declarations
    .map((declaration) => declaration.trim())
    .filter(Boolean)
    .map((declaration) => {
      const separator = declaration.indexOf(":");
      if (separator <= 0) {
        return null;
      }
      return {
        property: declaration.slice(0, separator).trim().toLowerCase(),
        value: declaration.slice(separator + 1).trim(),
      };
    });
  return parsed.some((declaration) => declaration === null)
    ? null
    : (parsed as CssDeclaration[]);
};

const validateStyle = (
  element: string,
  style: string,
  diagnostics: EmailThumbnailDiagnostic[]
): void => {
  const allowedProperties = STYLE_PROPERTIES[element] || [];
  const declarations = parseCssDeclarations(style);
  if (!declarations || !declarations.length) {
    diagnostics.push(
      error("forbidden-attribute", "Style declarations are required")
    );
    return;
  }
  declarations.forEach((declaration) => {
    const { property, value } = declaration;
    if (
      !allowedProperties.includes(property) ||
      !isAllowedStyleValue(property, value)
    ) {
      diagnostics.push(
        error(
          "forbidden-attribute",
          "Generated output contains unsupported CSS"
        )
      );
      return;
    }
    if (property === "background-image") {
      const urlMatch = /^url\((?:"([^"]*)"|'([^']*)'|([^\s()]+))\)$/i.exec(
        value
      );
      const url = urlMatch?.[1] ?? urlMatch?.[2] ?? urlMatch?.[3];
      if (!url || /^data:/i.test(url)) {
        diagnostics.push(
          error("data-url", "Generated output cannot contain data URLs")
        );
      }
      if (!url || !isHttpsUrl(url)) {
        diagnostics.push(
          error(
            "invalid-background-url",
            "Generated output URLs must be public absolute HTTPS URLs"
          )
        );
      }
    }
  });
};

const isAllowedStyleValue = (property: string, value: string): boolean => {
  if (property === "background-image") {
    return /^url\((?:"[^"]*"|'[^']*'|[^\s()]+)\)$/i.test(value);
  }
  if (property === "font-family") {
    return (FONT_FAMILIES as readonly string[]).includes(value);
  }
  if (property === "font-weight") {
    return value === "normal" || value === "bold";
  }
  if (property === "font-size") {
    return (
      /^\d+px$/.test(value) && FONT_SIZES.includes(Number.parseInt(value, 10))
    );
  }
  if (property === "line-height") {
    return (
      /^\d+px$/.test(value) && LINE_HEIGHTS.includes(Number.parseInt(value, 10))
    );
  }
  if (property === "color" || property === "background-color") {
    return HEX_COLOR.test(value);
  }
  if (property === "width" || property === "height") {
    return /^\d+px$/.test(value) || value === "100%";
  }
  if (property === "padding") {
    return (
      /^\d+px$/.test(value) && PADDINGS.includes(Number.parseInt(value, 10))
    );
  }
  if (property === "border-collapse") {
    return value === "collapse";
  }
  if (property === "background-position") {
    return value === "center";
  }
  if (property === "background-repeat") {
    return value === "no-repeat";
  }
  if (property === "background-size") {
    return value === "cover";
  }
  if (property === "text-decoration") {
    return value === "none";
  }
  if (property === "display") {
    return value === "block" || value === "inline-block";
  }
  if (property === "text-align") {
    return value === "center";
  }
  return false;
};

type VmlTag = {
  name: string;
  closing: boolean;
  selfClosing: boolean;
  attributes: string;
};

const VML_RECT_STYLE = /^width:\d+px;height:\d+px;$/;

const parseVmlAttributes = (source: string): Record<string, string> | null => {
  const attributes: Record<string, string> = {};
  const content = source.replace(/\/\s*$/, "");
  const attribute = /\s+([A-Za-z_:][A-Za-z0-9:._-]*)\s*=\s*(["'])(.*?)\2/g;
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = attribute.exec(content))) {
    if (!/^\s*$/.test(content.slice(cursor, match.index))) {
      return null;
    }
    const name = match[1].toLowerCase();
    if (Object.prototype.hasOwnProperty.call(attributes, name)) {
      return null;
    }
    attributes[name] = match[3];
    cursor = attribute.lastIndex;
  }
  return /^\s*$/.test(content.slice(cursor)) ? attributes : null;
};

const addVmlAttributeError = (
  diagnostics: EmailThumbnailDiagnostic[]
): void => {
  diagnostics.push(
    error(
      "forbidden-attribute",
      "Outlook VML contains an unsupported attribute"
    )
  );
};

const validateVmlAttributes = (
  tag: VmlTag,
  diagnostics: EmailThumbnailDiagnostic[]
): void => {
  if (tag.closing) {
    if (tag.attributes.trim()) {
      addVmlAttributeError(diagnostics);
    }
    return;
  }

  const attributes = parseVmlAttributes(tag.attributes);
  if (!attributes) {
    addVmlAttributeError(diagnostics);
    return;
  }
  const keys = Object.keys(attributes);

  if (tag.name === "v:rect") {
    if (
      keys.length !== 4 ||
      attributes["xmlns:v"] !== "urn:schemas-microsoft-com:vml" ||
      attributes.fill !== "true" ||
      attributes.stroke !== "false" ||
      !VML_RECT_STYLE.test(attributes.style)
    ) {
      addVmlAttributeError(diagnostics);
    }
    return;
  }

  if (tag.name === "v:fill") {
    if (
      keys.length !== 3 ||
      attributes.type !== "frame" ||
      !HEX_COLOR.test(attributes.color) ||
      !attributes.src
    ) {
      addVmlAttributeError(diagnostics);
      return;
    }
    validateUrlAttribute("src", attributes.src, diagnostics);
    return;
  }

  if (
    tag.name !== "v:textbox" ||
    keys.length !== 1 ||
    attributes.inset !== "0,0,0,0"
  ) {
    addVmlAttributeError(diagnostics);
  }
};

const tokenizeVml = (
  source: string,
  diagnostics: EmailThumbnailDiagnostic[]
): VmlTag[] => {
  const tags: VmlTag[] = [];
  const tagPattern = /<\s*(\/?)\s*([A-Za-z][A-Za-z0-9:-]*)([^>]*)>/g;
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = tagPattern.exec(source))) {
    if (!/^\s*$/.test(source.slice(cursor, match.index))) {
      diagnostics.push(
        error("forbidden-markup", "Outlook VML contains unexpected markup")
      );
    }
    const name = match[2].toLowerCase();
    const closing = match[1] === "/";
    const attributes = match[3];
    if (!["v:rect", "v:fill", "v:textbox"].includes(name)) {
      diagnostics.push(
        error(
          "forbidden-markup",
          `Outlook VML element <${name}> is not allowed`
        )
      );
    }
    tags.push({
      name,
      closing,
      selfClosing: !closing && /\/\s*$/.test(attributes),
      attributes,
    });
    cursor = tagPattern.lastIndex;
  }
  if (!/^\s*$/.test(source.slice(cursor))) {
    diagnostics.push(
      error("forbidden-markup", "Outlook VML contains unexpected markup")
    );
  }
  return tags;
};

const VML_OPENING_SEQUENCE: Array<
  Pick<VmlTag, "name" | "closing" | "selfClosing">
> = [
  { name: "v:rect", closing: false, selfClosing: false },
  { name: "v:fill", closing: false, selfClosing: true },
  { name: "v:textbox", closing: false, selfClosing: false },
];
const VML_CLOSING_SEQUENCE: Array<
  Pick<VmlTag, "name" | "closing" | "selfClosing">
> = [
  { name: "v:textbox", closing: true, selfClosing: false },
  { name: "v:rect", closing: true, selfClosing: false },
];

const hasExpectedVmlStructure = (
  tags: VmlTag[],
  expected: Array<Pick<VmlTag, "name" | "closing" | "selfClosing">>
): boolean => {
  return (
    tags.length === expected.length &&
    tags.every(
      (tag, index) =>
        tag.name === expected[index].name &&
        tag.closing === expected[index].closing &&
        tag.selfClosing === expected[index].selfClosing
    )
  );
};

type BackgroundCell = {
  node: ParsedNode;
  canvasTable?: ParsedNode;
  outerTable?: ParsedNode;
};

const getNodeAttribute = (
  node: ParsedNode,
  attribute: string
): string | undefined =>
  (node.attrs || []).find((item) => item.name.toLowerCase() === attribute)
    ?.value;

const findBackgroundCells = (
  node: ParsedNode,
  tableAncestors: ParsedNode[] = []
): BackgroundCell[] => {
  const currentTables =
    node.tagName?.toLowerCase() === "table"
      ? [...tableAncestors, node]
      : tableAncestors;
  const currentCell =
    node.tagName?.toLowerCase() === "td" &&
    getNodeAttribute(node, "background") !== undefined
      ? [
          {
            node,
            canvasTable: currentTables[currentTables.length - 1],
            outerTable: currentTables[0],
          },
        ]
      : [];
  return [
    ...currentCell,
    ...(node.childNodes || []).flatMap((child) =>
      findBackgroundCells(child, currentTables)
    ),
  ];
};

const parseSafePixelDimension = (value: string | undefined): number | null => {
  if (!value || !/^\d+$/.test(value)) {
    return null;
  }
  const dimension = Number(value);
  return Number.isSafeInteger(dimension) && dimension >= 1 && dimension <= 1200
    ? dimension
    : null;
};

const getPixelDimension = (
  node: ParsedNode | undefined,
  attribute: "width" | "height"
): number | null => {
  return getPixelDimensionSource(node, attribute).value;
};

type PixelDimensionSource = {
  present: boolean;
  value: number | null;
};

const getPixelDimensionSource = (
  node: ParsedNode | undefined,
  attribute: "width" | "height"
): PixelDimensionSource => {
  const value = node && getNodeAttribute(node, attribute);
  return {
    present: value !== undefined,
    value: parseSafePixelDimension(value),
  };
};

const getStylePixelDimensionSource = (
  node: ParsedNode | undefined,
  property: "width" | "height"
): PixelDimensionSource => {
  const style = node && getNodeAttribute(node, "style");
  const declarations = parseCssDeclarations(style || "");
  const values = (declarations || [])
    .filter((declaration) => declaration.property === property)
    .map((declaration) => declaration.value.replace(/px$/, ""));
  return {
    present: values.length > 0,
    value: values.length === 1 ? parseSafePixelDimension(values[0]) : null,
  };
};

const getStylePixelDimension = (
  node: ParsedNode | undefined,
  property: "width" | "height"
): number | null => {
  return getStylePixelDimensionSource(node, property).value;
};

const getVmlRectDimensions = (tag?: VmlTag): [number, number] | null => {
  if (!tag) {
    return null;
  }
  const attributes = parseVmlAttributes(tag.attributes);
  const match =
    attributes && /^width:(\d+)px;height:(\d+)px;$/.exec(attributes.style);
  const width = match && parseSafePixelDimension(match[1]);
  const height = match && parseSafePixelDimension(match[2]);
  return width && height ? [width, height] : null;
};

const isWhitespaceTextNode = (node: ParsedNode): boolean =>
  node.nodeName === "#text" && /^\s*$/.test(node.value || "");

const validateVmlCanvasPlacement = (
  root: ParsedNode | undefined,
  openingTags: VmlTag[],
  openingComment: string,
  closingComment: string,
  diagnostics: EmailThumbnailDiagnostic[]
): void => {
  const backgroundCells = root ? findBackgroundCells(root) : [];
  if (backgroundCells.length !== 1) {
    diagnostics.push(
      error(
        "missing-vml",
        "Outlook VML must be inside the single background cell"
      )
    );
    return;
  }

  const { node: backgroundCell, canvasTable, outerTable } = backgroundCells[0];
  const [vmlWidth, vmlHeight] = getVmlRectDimensions(openingTags[0]) || [];
  const tdWidth = getPixelDimension(backgroundCell, "width");
  const tdHeight = getPixelDimension(backgroundCell, "height");
  const tableWidth = getPixelDimension(canvasTable, "width");
  const tableHeight = getPixelDimension(canvasTable, "height");
  const tableStyleWidth = getStylePixelDimension(canvasTable, "width");
  const tableStyleHeight = getStylePixelDimension(canvasTable, "height");
  const outerWidth = getPixelDimension(outerTable, "width");
  const outerStyleWidth = getStylePixelDimension(outerTable, "width");
  const outerHeights = [
    getPixelDimensionSource(outerTable, "height"),
    getStylePixelDimensionSource(outerTable, "height"),
  ];
  const widths = [
    vmlWidth,
    tdWidth,
    tableWidth,
    tableStyleWidth,
    outerWidth,
    outerStyleWidth,
  ];
  const heights = [vmlHeight, tdHeight, tableHeight, tableStyleHeight];
  if (
    widths.some((dimension) => !dimension || dimension !== vmlWidth) ||
    heights.some((dimension) => !dimension || dimension !== vmlHeight) ||
    outerHeights.some(
      ({ present, value }) => present && (value === null || value !== vmlHeight)
    )
  ) {
    diagnostics.push(
      error(
        "forbidden-attribute",
        "Outlook VML dimensions must match the HTML canvas"
      )
    );
  }

  const children = (backgroundCell.childNodes || []).filter(
    (child) => !isWhitespaceTextNode(child)
  );
  const [opening, content, closing] = children;
  if (
    children.length !== 3 ||
    opening?.nodeName !== "#comment" ||
    opening.data !== openingComment ||
    content?.tagName?.toLowerCase() !== "a" ||
    closing?.nodeName !== "#comment" ||
    closing.data !== closingComment
  ) {
    diagnostics.push(
      error(
        "missing-vml",
        "Outlook VML comments must immediately bracket the background-cell content"
      )
    );
  }
};

const validateConditionalVml = (
  fragment: string,
  root: ParsedNode | undefined,
  diagnostics: EmailThumbnailDiagnostic[]
): void => {
  const conditionalStart = /<!--\s*\[if\b/gi;
  const conditionalComment = /<!--\s*\[if\b[\s\S]*?<!\[endif\]\s*-->/gi;
  const outlookBranch = /<!--\[if gte mso 9\]>([\s\S]*?)<!\[endif\]-->/g;
  const conditionalStarts = Array.from(fragment.matchAll(conditionalStart));
  const conditionalComments = Array.from(fragment.matchAll(conditionalComment));
  const branches = Array.from(fragment.matchAll(outlookBranch));
  if (
    conditionalStarts.length !== 2 ||
    conditionalComments.length !== 2 ||
    branches.length !== 2
  ) {
    diagnostics.push(
      error(
        "missing-vml",
        "Generated output must include the classic Outlook VML branch"
      )
    );
    return;
  }

  const openingTags = tokenizeVml(branches[0][1], diagnostics);
  const closingTags = tokenizeVml(branches[1][1], diagnostics);
  openingTags.forEach((tag) => validateVmlAttributes(tag, diagnostics));
  closingTags.forEach((tag) => validateVmlAttributes(tag, diagnostics));
  const hasValidVmlStructure =
    hasExpectedVmlStructure(openingTags, VML_OPENING_SEQUENCE) &&
    hasExpectedVmlStructure(closingTags, VML_CLOSING_SEQUENCE);
  if (!hasValidVmlStructure) {
    diagnostics.push(
      error(
        "missing-vml",
        "Generated output must include the classic Outlook VML branch"
      )
    );
    return;
  }
  validateVmlCanvasPlacement(
    root,
    openingTags,
    `[if gte mso 9]>${branches[0][1]}<![endif]`,
    `[if gte mso 9]>${branches[1][1]}<![endif]`,
    diagnostics
  );
};
